import { useTranslation } from "react-i18next";
import { type FormInstance } from "antd";
import { createStyles } from "antd-style";

import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Modal } from "@av/components/atoms/Modal";
import { Select } from "@av/components/atoms/Select";
import { mapErrors } from "../../../../../services/config/extract-validation-errors";

import type { ApiError, SelectOption } from "../../../../../services/admin-settings/type";

export type AssignUserFormValues = {
  securityGroups: string[];
  name: string;
  userId: string;
  email: string;
};

type AssignUserModalProps = {
  open: boolean;
  securityGroupOptions: SelectOption[] | undefined;
  onCancel: () => void;
  onSubmit: (values: AssignUserFormValues) => void;
  form: FormInstance<AssignUserFormValues>;
  serverError: ApiError | null;
  isSubmitting: boolean;
};

const useStyles = createStyles(({ css, token }) => ({
  form: css`
    .ant-form-item {
      margin-bottom: ${token.margin}px;
    }

    .ant-form-item-row {
      align-items: center;
    }

    .ant-form-item-label {
      padding-bottom: 0;
      text-align: left;
    }

    .ant-form-item-label > label {
      height: auto;
      color: ${token.colorTextSecondary};
      font-size: ${token.fontSizeLG}px;
    }
  `,
}));

export function AssignUserModal(props: AssignUserModalProps) {
  const { open, securityGroupOptions, onCancel, onSubmit, form, serverError, isSubmitting } = props;
  const { t } = useTranslation();
  const { styles } = useStyles();

  return (
    <Modal
      testIdPrefix="admin-settings-assign-user-security-groups"
      title={t("platform.adminSettings.users_dialog_edit", { name: form.getFieldValue("name") })}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      afterClose={() => form.resetFields()}
      okText={t("common.domainShared.global_save")}
      cancelText={t("common.domainShared.global_cancel")}
      okButtonProps={{ loading: isSubmitting }}
      forceRender
      width={772}
    >
      <Form
        serverErrors={mapErrors(t, serverError)}
        form={form}
        labelCol={{ flex: "150px" }}
        wrapperCol={{ flex: 1 }}
        layout="horizontal"
        requiredMark={false}
        onFinish={onSubmit}
        className={styles.form}
      >
        <Form.Item testIdPrefix="user-id-form" name="userId" hidden />
        <Form.Item
          testIdPrefix="admin-settings-assign-user-email"
          name="email"
          label={t("platform.adminSettings.users_dialog_loginEmail")}
        >
          <Input testIdPrefix="admin-settings-assign-user-login-email" />
        </Form.Item>
        <Form.Item
          testIdPrefix="admin-settings-assign-user-security-group"
          label={t("platform.adminSettings.users_dialog_securityGroup")}
          name="securityGroups"
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            testIdPrefix="admin-settings-assign-user-security-groups-security-groups"
            mode="multiple"
            options={securityGroupOptions}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
